// src/features/commodities-info/CommodityInfoPage.tsx
//
// Commodity Info Deep-Dive Page — Component Islands architecture for
// progressive loading and failure isolation. Extracts the pricing symbol from
// the URL query param (?symbol=GCUSD), fetches the static metadata
// (description + strategic playbook) from GET /api/v1/commodities-info/{symbol},
// and renders it alongside two quantitative visualization islands that reuse
// the existing commodities-ranking endpoints (Forge Ultra Signal trend + COT
// net positioning).
//
// Amber/gold accent (amber-500) distinguishes the Commodities asset class from
// Stocks (blue) and Crypto (orange/emerald). Accessed via the "More Info…"
// action in the Commodities Ranking table/popup.

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Layout, Sidebar } from '../../shared/ui';
import { fetchCommodityInfoBySymbol, type CommodityInfo } from './api';
import { CommodityCotChart } from './components/CommodityCotChart';
import { CommodityEtfsTable } from './components/CommodityEtfsTable';
import { CommodityHistoricalChart } from './components/CommodityHistoricalChart';
import { CommodityPriceVolumeChart } from './components/CommodityPriceVolumeChart';

export const CommodityInfoPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const symbol = searchParams.get('symbol')?.toUpperCase();

    const [info, setInfo] = useState<CommodityInfo | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!symbol) return;

        let cancelled = false;
        setIsLoading(true);
        setError(null);

        fetchCommodityInfoBySymbol(symbol)
            .then((data) => {
                if (!cancelled) setInfo(data);
            })
            .catch((err) => {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error(err);
                setError(`Unable to load fundamental data for ${symbol}.`);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol]);

    if (!symbol) {
        return (
            <Layout sidebar={<Sidebar />} title="Commodity Intelligence">
                <div className="p-6 text-slate-500 dark:text-slate-400">
                    No symbol provided. Please select an asset from the
                    Commodities Rankings.
                </div>
            </Layout>
        );
    }

    return (
        <Layout
            sidebar={<Sidebar />}
            title={`Deep Dive: ${symbol}`}
            subtitle={info?.commodity_name || 'Macro Analysis'}
        >
            <div className="mb-6">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="text-sm font-medium text-amber-600 dark:text-amber-500 hover:underline flex items-center gap-1"
                >
                    &larr; Back to Rankings
                </button>
            </div>

            {error && (
                <div className="mb-6 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="h-64 flex items-center justify-center text-slate-500 dark:text-slate-400 animate-pulse">
                    Loading qualitative metadata for {symbol}…
                </div>
            ) : (
                info && (
                    <div className="space-y-6">
                        {/* --- HEADER ISLAND --- */}
                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 border-l-4 border-l-amber-500 shadow-sm">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                                <div>
                                    <div className="flex items-center gap-3 mb-1">
                                        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
                                            {info.commodity_name || symbol}
                                        </h1>
                                        <span className="px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-mono font-bold rounded border border-slate-200 dark:border-slate-700">
                                            COMMODITY : {symbol}
                                        </span>
                                    </div>
                                    <p className="text-slate-500 dark:text-slate-400 text-sm">
                                        Fundamental Macroeconomic Profile
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* --- TEXT BLOCKS ISLAND --- */}
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm">
                                <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">
                                    Fundamental Overview
                                </h2>
                                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                                    {info.description ||
                                        'No description available.'}
                                </p>
                            </div>

                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm">
                                <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">
                                    Strategic Playbook
                                </h2>
                                {info.how_to_trade_this_commodity_strategy &&
                                info.how_to_trade_this_commodity_strategy
                                    .length > 0 ? (
                                    <ul className="space-y-3">
                                        {info.how_to_trade_this_commodity_strategy.map(
                                            (pt, i) => (
                                                <li
                                                    key={i}
                                                    className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed"
                                                >
                                                    <span className="text-amber-500 mt-0.5 shrink-0">
                                                        ✦
                                                    </span>
                                                    <span>{pt}</span>
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                ) : (
                                    <span className="text-sm text-slate-500 dark:text-slate-400">
                                        No strategy defined.
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* --- DATA VISUALIZATION ISLANDS --- */}
                        {/* 1. Historical signal trend + 2. live proxy ETFs. */}
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
                            <CommodityHistoricalChart symbol={symbol} />
                            <CommodityEtfsTable symbol={symbol} />
                        </div>

                        {/* 3. Interactive candlestick Price/Volume chart with
                            technical-indicator overlays (FMP
                            /stable/technical-indicators + historical-price-eod). */}
                        <CommodityPriceVolumeChart symbol={symbol} />

                        {/* 4. COT net-positioning trend (reuses the
                            commodities-ranking COT endpoint). */}
                        <div className="grid grid-cols-1 gap-6 items-stretch">
                            <CommodityCotChart symbol={symbol} />
                        </div>
                    </div>
                )
            )}
        </Layout>
    );
};

export default CommodityInfoPage;
